import React, { useMemo, useEffect, useState } from "react";
import { AlertTriangle, Clock, ChevronsUp } from "lucide-react";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

const PRI_CONFIG = {
  P1: { label: "P1 — Critical", color: "#f87171",  border: "border-red-500/30",    bg: "bg-red-500/5"    },
  P2: { label: "P2 — High",     color: "#fb923c",  border: "border-orange-500/30", bg: "bg-orange-500/5" },
  P3: { label: "P3 — Medium",   color: "#fbbf24",  border: "border-yellow-500/30", bg: "bg-yellow-500/5" },
  P4: { label: "P4 — Low",      color: "#60a5fa",  border: "border-blue-500/30",   bg: "bg-blue-500/5"   },
};

function SlaCountdown({ due }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(t); }, []);
  if (!due) return null;
  const ms   = new Date(due).getTime() - now;
  const hrs  = Math.floor(Math.abs(ms) / 3600000);
  const mins = Math.floor((Math.abs(ms) % 3600000) / 60000);
  const overdue = ms < 0;
  return (
    <span className={`flex items-center gap-1 text-xs ${overdue ? "text-red-400" : hrs < 2 ? "text-yellow-400" : "text-white/30"}`}>
      <Clock size={10} />
      {overdue ? `-${hrs}h${mins}m` : `${hrs}h${mins}m`}
    </span>
  );
}

export default function PriorityQueues({ tickets, onPick, onEscalate }) {
  const buckets = useMemo(() => {
    const map = { P1: [], P2: [], P3: [], P4: [] };
    (tickets || []).forEach(t => { if (map[t.priority]) map[t.priority].push(t); });
    return map;
  }, [tickets]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {["P1","P2","P3","P4"].map(p => {
        const cfg   = PRI_CONFIG[p];
        const items = buckets[p] || [];
        return (
          <div key={p} className={`rounded-xl border ${cfg.border} bg-[#0d0d0d]`}>
            <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
              <span className="text-xs font-semibold" style={{ color: cfg.color }}>{cfg.label}</span>
              <span className="text-xs text-white/30">{items.length}</span>
            </div>
            <div className="p-3 space-y-2 max-h-72 overflow-y-auto">
              {items.length === 0 ? (
                <p className="text-white/20 text-xs italic text-center py-3">No tickets.</p>
              ) : items.map(t => (
                <div key={t.id} className={`rounded-lg border border-white/5 ${t.breached ? "bg-red-500/5 border-red-500/20" : "bg-white/3"} p-3`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-xs text-white/70 font-medium leading-snug">{t.subject}</p>
                    {t.breached && <AlertTriangle size={12} className="text-red-400 shrink-0 mt-0.5" />}
                  </div>
                  <p className="text-xs text-white/30 mb-2">{t.id} · {t.category} · {t.requester}</p>
                  <div className="flex items-center justify-between gap-2">
                    <SlaCountdown due={t.due} />
                    <div className="flex gap-1.5">
                      <button onClick={() => onPick?.(t)}
                        className="text-xs px-2 py-1 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
                        Open
                      </button>
                      <button onClick={() => onEscalate?.(t.id)}
                        className="text-xs px-2 py-1 rounded-lg border border-orange-500/20 bg-orange-500/5 text-orange-400/60 hover:bg-orange-500/15 hover:text-orange-400 transition-colors">
                        <ChevronsUp size={11} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}